import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import AppHeader from "@/components/AppHeader";
import ActivityCard from "@/components/ActivityCard";
import api from "@/lib/api";
import { SPORTS } from "@/lib/sports";
import { useI18n } from "@/context/I18nContext";
import { Crosshair } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Explore() {
  const { t, lang } = useI18n();
  const [sp, setSp] = useSearchParams();
  const sport = sp.get("sport") || "";
  const skill = sp.get("skill_level") || "";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [near, setNear] = useState(null); // {lat, lng}
  const [radius, setRadius] = useState(25);
  const [sortMode, setSortMode] = useState("upcoming"); // upcoming | distance

  useEffect(() => {
    setLoading(true);
    const q = new URLSearchParams();
    if (sport) q.set("sport", sport);
    if (skill) q.set("skill_level", skill);
    q.set("sort", near && sortMode === "distance" ? "distance" : "upcoming");
    if (near) {
      q.set("near", `${near.lat},${near.lng}`);
      q.set("radius_km", String(radius));
    }
    api.get(`/activities?${q.toString()}`).then((r) => setItems(r.data || [])).finally(() => setLoading(false));
  }, [sport, skill, near, radius, sortMode]);

  const setSport = (s) => {
    const next = new URLSearchParams(sp);
    if (s) next.set("sport", s); else next.delete("sport");
    next.delete("skill_level");
    setSp(next);
  };
  const setSkill = (l) => {
    const next = new URLSearchParams(sp);
    if (l) next.set("skill_level", l); else next.delete("skill_level");
    setSp(next);
  };

  const toggleNear = () => {
    if (near) { setNear(null); return; }
    if (!navigator.geolocation) { toast.error("Geolocation not supported"); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => setNear({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => toast.error("Couldn't get your location")
    );
  };

  const activeSport = SPORTS.find((s) => s.key === sport);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <AppHeader />
      <main className="max-w-7xl mx-auto px-6 py-10">
        <div className="label-overline text-[#007AFF]">{t("explore.kicker")}</div>
        <h1 className="font-display text-5xl tracking-tight mt-2">{t("explore.title")}</h1>

        <div className="mt-8 flex flex-wrap gap-2 items-center">
          <Chip active={!sport} onClick={() => setSport("")} testId="filter-all">{t("common.all")}</Chip>
          {SPORTS.map((s) => (
            <Chip key={s.key} active={sport === s.key} onClick={() => setSport(s.key)} testId={`filter-${s.key}`}>{s.label}</Chip>
          ))}
          <div className="ml-auto flex items-center gap-2">
            {near && (
              <>
                <button
                  data-testid="sort-distance-toggle"
                  onClick={() => setSortMode((m) => (m === "distance" ? "upcoming" : "distance"))}
                  className={`kinetic-btn px-3 py-2 text-xs uppercase tracking-widest font-bold border ${sortMode === "distance" ? "bg-[#007AFF] border-[#007AFF] text-white" : "border-[#262626] hover:border-white/40 bg-[#141414]"}`}
                >
                  {t("explore.kicker") && (lang === "da" ? "Nærmest først" : "Nearest first")}
                </button>
                <select
                  data-testid="radius-select"
                  value={radius}
                  onChange={(e) => setRadius(Number(e.target.value))}
                  className="bg-[#141414] border border-[#262626] text-xs uppercase tracking-widest font-bold px-3 py-2"
                >
                  {[5, 10, 25, 50, 100].map((r) => (
                    <option key={r} value={r}>{r} km</option>
                  ))}
                </select>
              </>
            )}
            <button
              data-testid="near-me-toggle"
              onClick={toggleNear}
              className={`kinetic-btn px-4 py-2 text-xs uppercase tracking-widest font-bold border flex items-center gap-2 ${near ? "bg-[#007AFF] border-[#007AFF] text-white" : "border-[#262626] hover:border-white/40 bg-[#141414]"}`}
            >
              <Crosshair weight="bold" size={14} />
              {t("explore.near_me")}
            </button>
          </div>
        </div>

        {activeSport && (
          <div className="mt-3 flex flex-wrap gap-2">
            <Chip small active={!skill} onClick={() => setSkill("")} testId="skill-all">{t("explore.all_levels")}</Chip>
            {activeSport.levels.map((l) => (
              <Chip key={l} small active={skill === l} onClick={() => setSkill(l)} testId={`skill-${l}`}>{l}</Chip>
            ))}
          </div>
        )}

        <div className="mt-8">
          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {["sk1", "sk2", "sk3", "sk4", "sk5", "sk6"].map((k) => <div key={k} className="tf-card h-44 animate-pulse" />)}
            </div>
          ) : items.length === 0 ? (
            <div className="border border-dashed border-[#262626] p-8 text-sm text-zinc-500">{t("explore.no_match")}</div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {items.map((a) => <ActivityCard key={a.activity_id} activity={a} />)}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

function Chip({ active, onClick, children, small, testId }) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      className={`kinetic-btn ${small ? "px-3 py-1.5 text-xs" : "px-4 py-2 text-sm"} uppercase tracking-widest font-semibold border ${active ? "bg-[#007AFF] border-[#007AFF] text-white" : "border-[#262626] hover:border-white/40 bg-[#141414] text-zinc-200"}`}
    >
      {children}
    </button>
  );
}
